import {Component, PanelLifecycleContext} from 'panel';

import {Subscribable} from 'common/types/context/subscribable';
import {TourData, TourName} from 'common/util/tour-constants';
import {getDownloadMode} from 'common/report/util';
import {DownloadMode, LocalStorageKey} from 'common/report/constants';
import {CurrentProjectResource} from 'common/report/model-resources/projects';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {DISMISS_KEY, PersistedDismissTooltipTour} from 'common/components/tooltip-tour/types';
import {getCurrentTooltipTourList, userDismissedTheCurrentTooltipTour} from 'common/components/tooltip-tour/utils';
import {StorageManager} from 'common/contexts/storage-manager/types';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {isTestEnv} from 'common/report/util/env';
import {getCurrentStackingContextDepth} from 'common/components/base/stacking-context';

export class TourManager implements PanelLifecycleContext, Subscribable {
  declare currentProject: CurrentProjectResource;
  private connectedComponents = new Set<Component<any>>();
  private subscriptions = new Set<() => void>();
  private declare userLocalStorageDismissTipsHelper: UserLocalStorageHelper<PersistedDismissTooltipTour>;

  private toursQueue: Map<TourName, HTMLElement | null> = new Map();
  private registeredTours: Map<TourName, TourData> = new Map();

  public activeTourName: TourName | null = null;
  public activeTargetEl: HTMLElement | null = null;
  private _activeTourStep: number = 0;

  constructor({
    currentProject,
    localStorageManager,
  }: {
    currentProject: CurrentProjectResource;
    localStorageManager: StorageManager;
  }) {
    this.currentProject = currentProject;

    this.userLocalStorageDismissTipsHelper = new UserLocalStorageHelper({
      context: localStorageManager,
      namespace: LocalStorageKey.DismissTooltipTour,
    });
  }

  public registerTour(tourName: TourName, tourData: TourData): void {
    this.registeredTours.set(tourName, tourData);
  }

  get activeTour(): TourData | null {
    const activeTour = this.activeTourName ? this.registeredTours.get(this.activeTourName) : null;
    return activeTour ?? null;
  }

  get activeTourStep(): number {
    return this._activeTourStep;
  }

  public nextStep(): void {
    if (!this.activeTour) {
      return;
    } else if (this._activeTourStep + 1 >= this.activeTour.stepData.length) {
      this.dismissTour();
      return;
    }
    this._activeTourStep++;
    this.updateComponents();
  }

  public prevStep(): void {
    if (!this.activeTour) {
      return;
    } else if (this.activeTourStep === 0) {
      this.dismissTour();
      return;
    }

    this._activeTourStep--;
    this.updateComponents();
  }

  private showTour(tour: TourName | null): void {
    if (tour === null) {
      this.activeTourName = null;
      this.activeTargetEl = null;
    } else {
      this.activeTourName = tour;
      const targetEl = this.toursQueue.get(tour);
      this.activeTargetEl = targetEl?.isConnected ? targetEl : null;
      mixpanel.track(`Tour Modal Shown`, {
        'tour id': tour,
      });
    }
    this._activeTourStep = 0;
    this.updateComponents();
  }

  public openTour(tour: TourName, targetEl: HTMLElement | null = null): void {
    // prevents tours from showing up in chromatic tests
    if (isTestEnv() || this.toursQueue.has(tour) || !this.registeredTours.has(tour) || !this.shouldShowTour(tour)) {
      return;
    }

    this.toursQueue.set(tour, targetEl);

    if (this.toursQueue.size === 1) {
      this.showTour(tour);
    }
  }

  private isTourActive(expirationDate: Date | undefined): boolean {
    if (!expirationDate) {
      return true;
    }

    const currentDate = new Date();
    return currentDate < expirationDate;
  }

  private isEligible(featureGateTier: string[] | undefined) {
    if (!featureGateTier) {
      return true;
    }

    return (
      Boolean(this.currentProject?.id) &&
      featureGateTier.includes(this.currentProject.get().value?.featureGateTier ?? ``)
    );
  }

  /** @todo -- there is a known issue where if the current project data has not loaded yet, the tour will not be initialized */
  private shouldShowTour(tour: TourName): boolean {
    const tourData = this.registeredTours.get(tour);
    if (!tourData) {
      return false;
    }

    const {expirationDate, featureGateTier} = tourData;

    const hasDismissed = userDismissedTheCurrentTooltipTour(tour, this.userLocalStorageDismissTipsHelper);

    return (
      getDownloadMode() === DownloadMode.None &&
      this.isTourActive(expirationDate) &&
      this.isEligible(featureGateTier) &&
      !hasDismissed &&
      getCurrentStackingContextDepth() === 0 &&
      !this.currentProject.get().value?.isDemo
    );
  }

  public dismissTour({tour: dismissTourName, persist = true}: {tour?: TourName; persist?: boolean} = {}): void {
    const tour = this.activeTourName;

    // check if the consumer is asking to dismiss a specific tour
    if (!tour || (dismissTourName && dismissTourName !== tour)) {
      return;
    }

    this.toursQueue.delete(tour);
    mixpanel.track(`Tour Modal Dismissed`, {
      'tour id': tour,
    });

    if (this.toursQueue.size >= 1) {
      this.showTour(this.toursQueue.keys().next().value!);
    } else {
      this.showTour(null);
    }

    if (persist) {
      this.persistTourDismissal(tour);
    }
  }

  persistTourDismissal(tour: TourName) {
    const dismissedTooltipTourNames = getCurrentTooltipTourList(this.userLocalStorageDismissTipsHelper);

    if (!dismissedTooltipTourNames.includes(tour)) {
      dismissedTooltipTourNames.push(tour);
    }

    const userId = userCookieUtils.getUserIdentityFromCookie()?.id;
    this.userLocalStorageDismissTipsHelper.setItem({
      key: DISMISS_KEY,
      userId: userId!,
      value: {tips: dismissedTooltipTourNames},
    });
  }

  public reset() {
    this.showTour(null);
  }

  public subscribe(callback: () => void): this {
    this.subscriptions.add(callback);
    return this;
  }

  public unsubscribe(callback: () => void): this {
    this.subscriptions.delete(callback);
    return this;
  }

  public bindToComponent(component: Component<any>): void {
    this.connectedComponents.add(component);
  }

  public unbindFromComponent(component: Component<any>): void {
    this.connectedComponents.delete(component);
  }

  updateComponents = () => {
    for (const component of this.connectedComponents) {
      component.update();
    }

    for (const subscription of this.subscriptions) {
      subscription();
    }
  };
}
